import React, { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import WeaverLogo from "./WeaverLogo";

const NAV_LINKS = [
  { label: "WORK", href: "#featured-video" },
  { label: "ABOUT", href: "#about" },
  { label: "USE CASES", href: "#use-cases" },
  { label: "CLIENTS", href: "#clients" },
  { label: "SUBSCRIPTION", to: "/subscription" },
];

const Header = ({ onContact }) => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleAnchor = (href) => {
    setMobileOpen(false);
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-black/85 backdrop-blur-xl border-b border-white/10 py-3"
          : "bg-transparent py-6"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-12 flex items-center justify-between">
        <button
          onClick={() => handleAnchor("#hero")}
          className="focus:outline-none"
          data-testid="header-logo-link"
          aria-label="Go to top"
        >
          <WeaverLogo size="md" withMark data-testid="header-logo" />
        </button>

        {/* Desktop nav */}
        <nav className="hidden md:flex items-center gap-10" aria-label="Primary">
          {NAV_LINKS.map((link) =>
            link.to ? (
              <Link
                key={link.to}
                to={link.to}
                data-testid={`nav-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
                className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-300 hover:text-white transition-colors duration-300"
              >
                {link.label}
              </Link>
            ) : (
              <button
                key={link.href}
                onClick={() => handleAnchor(link.href)}
                data-testid={`nav-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
                className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-300 hover:text-white transition-colors duration-300"
              >
                {link.label}
              </button>
            )
          )}
          <button
            onClick={onContact}
            data-testid="nav-contact-btn"
            className="text-xs font-bold uppercase tracking-[0.2em] px-5 py-2.5 bg-white text-black hover:bg-zinc-200 transition-colors duration-300"
          >
            Contact
          </button>
        </nav>

        {/* Mobile toggle */}
        <button
          onClick={() => setMobileOpen((v) => !v)}
          className="md:hidden text-white p-2 -mr-2"
          data-testid="mobile-menu-toggle"
          aria-label={mobileOpen ? "Close menu" : "Open menu"}
          aria-expanded={mobileOpen}
        >
          {mobileOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Mobile menu */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.25 }}
            className="md:hidden bg-black/95 backdrop-blur-xl border-t border-white/10"
            data-testid="mobile-menu"
          >
            <nav className="flex flex-col px-6 py-8 gap-6" aria-label="Mobile">
              {NAV_LINKS.map((link) =>
                link.to ? (
                  <Link
                    key={link.to}
                    to={link.to}
                    onClick={() => setMobileOpen(false)}
                    data-testid={`mobile-nav-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
                    className="text-left text-sm font-bold uppercase tracking-[0.25em] text-zinc-200 hover:text-white"
                  >
                    {link.label}
                  </Link>
                ) : (
                  <button
                    key={link.href}
                    onClick={() => handleAnchor(link.href)}
                    data-testid={`mobile-nav-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
                    className="text-left text-sm font-bold uppercase tracking-[0.25em] text-zinc-200 hover:text-white"
                  >
                    {link.label}
                  </button>
                )
              )}
              <button
                onClick={() => {
                  setMobileOpen(false);
                  onContact();
                }}
                data-testid="mobile-nav-contact-btn"
                className="mt-2 w-full text-sm font-bold uppercase tracking-[0.25em] px-6 py-4 bg-white text-black"
              >
                Make Contact
              </button>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Header;
